import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import {
  PlaidEmbeddedLink,
  PlaidEmbeddedLinkPropTypes,
  PlaidLinkOnSuccess,
} from "react-plaid-link";

import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { FKC_ENTRIES_PER_CARD, FKC_MAX_CARD_COUNT, FKC_MAX_ENTRIES } from "../../fkc.constants";
import { FKCService } from "../../fkc.service";
import {
  linkedCardsAtom,
  pagesAtom,
  progressAtom,
  userDataAtom,
} from "../../fkc.state";
import { FallbackPages } from "../../fkc.types";
import { getFkcAddedCards } from "../../utils";

export function PlaidPage(): JSX.Element {
  const [token, setToken] = useState<string>("");
  const [hasReachedMaxCards, setHasReachedMaxCards] = useState<boolean>(false);

  const [, setPage] = useAtom(pagesAtom);
  const [linkedCards, setLinkedCards] = useAtom(linkedCardsAtom);
  const [userData] = useAtom(userDataAtom);
  const [, setProgress] = useAtom(progressAtom);

  const handleWidgetError = useErrorHandler();

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (public_token, { institution, accounts }) => {
      if (!userData || !institution) {
        return handleWidgetError(
          new Error("User data or institution not found")
        );
      }

      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") ?? "";
      const allowedCards = getFkcAddedCards(
        accounts.length,
        userData.cards_count
      );

      FKCService
        .savePublicToken(userData.publisher_user_id, {
          institution_id: institution.institution_id,
          institution_name: institution.name,
          public_token,
        })
        .then(() =>
          updateFormioSubmission(
            sweepFormUrl,
            {
              fkc_added_cards: allowedCards,
              ...submission,
            },
            handleWidgetError
          )
            .then(window.theme.Sweepstake.prototype.storeSubmission)
            .then(() => setLinkedCards(allowedCards))
            .then(() => setHasReachedMaxCards(allowedCards + userData.cards_count === FKC_MAX_CARD_COUNT))
            .then(() => setProgress(100))
        )
        .catch(handleWidgetError);
    },
    [handleWidgetError, userData, setLinkedCards, setProgress]
  );

  const config: PlaidEmbeddedLinkPropTypes = {
    className: "fkc__plaid",
    token,
    onSuccess,
  };

  useEffect(() => {
    if (!userData) {
      return handleWidgetError(new Error("User data not found"));
    }

    FKCService.getLinkToken(userData.publisher_user_id)
      .then(setToken)
      .catch(() => setPage(FallbackPages.PLAID_RETRY));
  }, [userData, setPage, handleWidgetError]);

  if (linkedCards) {
    const earnedEntries = linkedCards * FKC_ENTRIES_PER_CARD;
    const maxCardsMessage = `
      You have reached the maximum number of cards allowed and have earned a total of ${FKC_MAX_ENTRIES} entries.
      `;
    const linkCardsMessage = "Link more cards in your next submission to earn more entries.";
    const nextStepMessage = hasReachedMaxCards ? maxCardsMessage : linkCardsMessage;

    return (
      <>
        <strong className={clsx("fkc__children--title", "confetti")}>
          You have earned {earnedEntries} additional entries! &nbsp;
          {nextStepMessage}
        </strong>
        <PlaidEmbeddedLink {...config} />
        <p className="fkc__content__header--description">
          We&apos;ve emailed you a link to your new FindKeepCash account. Check
          your email when time allows.
        </p>
      </>
    );
  }

  return (
    <>
      <strong className="fkc__children--title">
        Link a card to unlock hundreds of automatic rewards and earn additional
        entries!
      </strong>
      <PlaidEmbeddedLink {...config} />
    </>
  );
}
